import { Layers3 } from 'lucide-react';
import { Card } from '@ai-tutor/ui';
import type { HighlightCarousel } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  TemplateCard,
  pickTemplateForConcept,
} from '@/components/features/highlights-template';
import { posterCard } from '@/components/features/highlights-template/poster';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { stripMath } from '@/lib/markdown/previewText';

/**
 * Overview grid for the lesson's highlights: one square tile per concept,
 * under a heading for each section of the lesson's plan (in teaching order).
 *
 * Each tile previews the concept's first card (template thumbnail), shows the
 * concept name, and - for multi-card concepts - a subtle "stacked cards"
 * affordance plus a count chip so the learner can see how many concepts exist
 * and how deep each one is before diving into the full-screen reader.
 *
 * Kept local to the lesson viewer because it consumes the lesson's finite
 * `HighlightCarousel[]`, not Mindspace's paginated feed.
 */

interface HighlightsConceptGridProps {
  carousels: HighlightCarousel[];
  /** Open the full-screen reader at the given concept index. */
  onOpen: (conceptIdx: number) => void;
}

export function HighlightsConceptGrid({
  carousels,
  onOpen,
}: HighlightsConceptGridProps) {
  const groups = sectionGroups(carousels);
  const tile = (idx: number) => (
    <HighlightConceptTile
      key={`${carousels[idx].concept_id}-${idx}`}
      carousel={carousels[idx]}
      onClick={() => onOpen(idx)}
    />
  );
  // No section on any deck (an older lesson, no plan): one grid, as before.
  if (groups.every(g => g.index === null)) {
    return (
      <div className='h-full overflow-y-auto p-4'>
        <div className={GRID}>{carousels.map((_, idx) => tile(idx))}</div>
      </div>
    );
  }
  return (
    <div className='h-full space-y-8 overflow-y-auto p-4'>
      {groups.map(group => (
        <section
          key={`${group.index ?? 'more'}-${group.decks[0]}`}
          aria-label={group.title ?? 'More highlights'}
        >
          <h3 className='mb-3 flex items-baseline gap-2.5'>
            {group.index !== null && (
              <span className='flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full border border-brand-500/40 bg-brand-500/10 font-mono text-xs font-medium text-foreground dark:border-primary/40 dark:bg-primary/15'>
                {group.index}
              </span>
            )}
            <InlineMarkdown className='text-sm font-semibold text-foreground'>
              {group.title ?? 'More highlights'}
            </InlineMarkdown>
          </h3>
          <div className={GRID}>{group.decks.map(tile)}</div>
        </section>
      ))}
    </div>
  );
}

const GRID = 'grid grid-cols-2 gap-3 md:grid-cols-3';

/** Consecutive decks of one plan section, with that section's number and
 *  title (`null` for decks outside the plan). Each deck keeps its index in
 *  `carousels`, which the reader walks in order. */
export function sectionGroups(
  carousels: HighlightCarousel[]
): { index: number | null; title: string | null; decks: number[] }[] {
  const groups: {
    index: number | null;
    title: string | null;
    decks: number[];
  }[] = [];
  carousels.forEach((c, idx) => {
    const index = c.section_index ?? null;
    const last = groups[groups.length - 1];
    if (last && last.index === index) last.decks.push(idx);
    else groups.push({ index, title: c.section_title ?? null, decks: [idx] });
  });
  return groups;
}

function HighlightConceptTile({
  carousel,
  onClick,
}: {
  carousel: HighlightCarousel;
  onClick: () => void;
}) {
  const firstCard = posterCard(carousel.cards);
  if (!firstCard) return null;

  const count = carousel.cards.length;
  const hasMultiple = count > 1;

  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={`Open ${stripMath(carousel.concept_name)} highlights (${count} card${count === 1 ? '' : 's'})`}
      className='group block text-left transition-transform duration-200 hover:scale-[1.01] focus-visible:outline-none'
    >
      <div className='relative'>
        {/* Subtle stack: one offset layer peeking behind to whisper "more
            underneath". Only for multi-card concepts. */}
        {hasMultiple && (
          <span
            aria-hidden
            className='absolute inset-0 -z-10 translate-x-1.5 translate-y-1.5 rounded-xl border border-border/70 bg-card/50 dark:bg-secondary'
          />
        )}

        <Card
          className={cn(
            'relative flex aspect-square flex-col overflow-hidden p-0 backdrop-blur-none transition-shadow duration-200',
            'group-hover:shadow-md group-focus-visible:ring-2 group-focus-visible:ring-brand-500/40 dark:focus-visible:ring-primary/40 dark:group-focus-visible:ring-primary/40'
          )}
        >
          <TemplateCard
            template={pickTemplateForConcept(carousel.concept_id)}
            card={firstCard}
            slideIndex={0}
            totalSlides={count}
            conceptName={carousel.concept_name}
            variant='thumbnail'
          />

          {/* Card-count chip, top-right: the templates put their own
              "SWIPE" / arrow cue in the bottom-right corner. */}
          {hasMultiple && (
            <span className='absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm'>
              <Layers3 className='h-3 w-3' />
              {count}
            </span>
          )}
        </Card>
      </div>

      <p className='mt-2 truncate text-sm font-medium text-foreground'>
        <InlineMarkdown>{carousel.concept_name}</InlineMarkdown>
      </p>
    </button>
  );
}
